import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  GroundworksTableComponent,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  ModalHeaderComponent,
} from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BoundProductInfo, TaxInfo } from '../../sdk/sdk';
import { ModalService } from '../../services/modal.service';
import { ProjectService } from '../../services/project.service';
import { parseMoney, prorateAmount, roundMoney } from '../../utils/money';
import {
  buildCurrentProjectTable,
  buildNewProjectTable,
  buildSplitLineViews,
  buildSplitTaxAllocations,
  SplitLineView,
  SplitTaxAvailability,
} from './split-project-tables';

export const SPLIT_MINIMUMS_MESSAGE =
  'You must enter at least 1 Unit and $1 to the New Project in order to save the projects. ' +
  'Would you like to resume splitting the project?';

// Copy pending design review (source-retention rule mirrors the New Project minimums).
export const SPLIT_SOURCE_FLOOR_MESSAGE =
  'The Current Project must retain at least 1 Unit and $1 in order to be completed. ' +
  'Would you like to resume splitting the project?';

export const SPLIT_COMPLETION_FAILED_MESSAGE =
  'The project was split, but completing it failed. Use Complete Project to finish it.';

export const SPLIT_TAX_AVAILABILITY_FAILED_MESSAGE =
  'Could not load how much tax is available to move, so the amounts below fall back to the ' +
  'job totals. Saving still validates them — reopen the modal to try again.';

/**
 * Split & Complete (MOD-3972): reached only from the commercial completion modal. Moves
 * partial product quantities/amounts onto ONE new auto-named Commercial project and completes
 * the CURRENT project server-side in the same call; the new project stays incomplete so it can
 * be split again at its own completion. Two side-by-side tables: the read-only Current Project
 * and the editable New Project, where the Units input drives the Total (prorated net) and the
 * $-Total stays manually overridable. Both sides must keep at least 1 unit and $1; an invalid
 * save opens a nested error modal whose Cancel path abandons the whole split.
 */
@Component({
  selector: 'app-split-project-modal',
  imports: [
    GroundworksTableComponent,
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
  ],
  templateUrl: './split-project-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SplitProjectModalComponent {
  private readonly projectService = inject(ProjectService);
  private readonly modalService = inject(ModalService);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly destroyRef = inject(DestroyRef);

  // Writable signals used instead of input() because NgbModal sets values imperatively.
  readonly projectId = signal('');
  readonly boundProducts = signal<BoundProductInfo[]>([]);
  readonly taxes = signal<TaxInfo[]>([]);
  readonly categoryByBoundProductId = signal<ReadonlyMap<string, string>>(new Map());

  readonly lines = signal<SplitLineView[]>([]);
  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);

  // Manual tax allocations keyed by taxTypeId; MAP MEMBERSHIP is the touched flag. Untouched
  // types are absent and keep auto-following the proportional preview as product moves change —
  // the same "derived until manually overridden" contract the product Units→Total inputs follow.
  private readonly manualTaxAllocations = signal<ReadonlyMap<string, number>>(new Map());

  // The server's per-type allocation pool, keyed by taxTypeId. NOT the job-level tax amount: once
  // a split has partitioned a type, only the anchor's remainder can move (0 for a type the
  // partition left nothing of). null until the pre-flight resolves — see buildSplitTaxAllocations.
  private readonly taxAvailability = signal<SplitTaxAvailability>(null);

  readonly taxAllocations = computed(() => {
    const newSubtotal = roundMoney(this.lines().reduce((sum, line) => sum + line.moveNet, 0));
    return buildSplitTaxAllocations(
      this.taxes(), newSubtotal, this.manualTaxAllocations(), this.taxAvailability());
  });

  readonly hasMovement = computed(() =>
    this.lines().some((line) => line.moveUnits > 0 || line.moveNet > 0),
  );

  readonly canSave = computed(() => {
    const units = this.lines().reduce((sum, line) => sum + line.moveUnits, 0);
    const net = roundMoney(this.lines().reduce((sum, line) => sum + line.moveNet, 0));
    return units >= 1 && net >= 1;
  });

  // The source completes as part of the split, so IT must keep real value. Sibling
  // non-installable lines (projectId ≠ this project, MOD-4278) have no retention floor and are
  // excluded; a missing projectId counts as source — that can only make the floor stricter.
  readonly sourceFloorMet = computed(() => {
    const sourceLines = this.lines().filter((line) => this.isSourceLine(line));
    const retainedUnits = sourceLines.reduce((sum, line) => sum + (line.originalUnits - line.moveUnits), 0);
    const retainedNet = roundMoney(sourceLines.reduce((sum, line) => sum + (line.originalNet - line.moveNet), 0));
    return retainedUnits >= 1 && retainedNet >= 1;
  });

  readonly currentTable = computed(() => buildCurrentProjectTable(this.lines(), this.taxAllocations()));

  readonly newTable = computed(() =>
    buildNewProjectTable(this.lines(), this.taxAllocations(), {
      onUnitsChange: (id, value) => this.onUnitsChange(id, value),
      onTotalChange: (id, value) => this.onTotalChange(id, value),
      onTaxAllocationChange: (taxTypeId, value) => this.onTaxAllocationChange(taxTypeId, value),
    }),
  );

  readonly footerActions = computed<ModalFooterAction[]>(() => [
    {
      label: 'Cancel Split',
      type: 'secondary',
      action: () => this.cancel(),
    },
    {
      // MOD-4277 AC2 / MOD-4278 AC1: the modal's submit is "Save & Complete" — the
      // "Split & Complete" label belongs to the ENTRY button in the completion modal.
      label: 'Save & Complete',
      type: 'primary',
      action: () => this.onSave(),
      // Disabled only while pristine (Figma empty state); an entered-but-invalid split must
      // stay clickable so the mandated nested error modal is reachable (post-it 3).
      disabled: () => !this.hasMovement() || this.loading(),
      loading: () => this.loading(),
    },
  ]);

  // Guards the one-shot availability fetch: NgbModal sets projectId imperatively after
  // construction, so the effect below is what learns the id — and it re-runs on every signal it
  // reads. Tracking the id already requested keeps it to a single request per project.
  private availabilityRequestedFor: string | null = null;

  constructor() {
    effect(() => {
      this.lines.set(buildSplitLineViews(this.boundProducts(), this.categoryByBoundProductId()));
    });

    effect(() => {
      const projectId = this.projectId();
      if (!projectId || this.availabilityRequestedFor === projectId) return;
      this.availabilityRequestedFor = projectId;
      this.loadTaxAvailability(projectId);
    });
  }

  private loadTaxAvailability(projectId: string): void {
    this.projectService
      .getSplitTaxAvailability(projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (availability) =>
          this.taxAvailability.set(
            new Map(availability.map((tax) => [tax.taxTypeId, tax.availableToAllocate]))),
        // Surfaced, not swallowed — but non-blocking: the tax inputs pin to 0 so a product-only
        // split stays possible. Falling back to the job-level amounts instead would auto-fill a
        // preview that is only correct on a first split, and the save's 400 refuses the product
        // moves along with the tax.
        error: () => {
          this.taxAvailability.set('unavailable');
          this.errorMessage.set(SPLIT_TAX_AVAILABILITY_FAILED_MESSAGE);
        },
      });
  }

  onUnitsChange(boundProductId: string, value: string | number): void {
    this.updateLine(boundProductId, (line) => {
      const requested = Math.floor(Number(value));
      const units = Number.isFinite(requested)
        ? Math.min(Math.max(requested, 0), line.originalUnits)
        : 0;
      // Prorating in integer cents keeps a full move exact (9 of 9 units moves the full net,
      // never units × rounded-unit-price).
      return { ...line, moveUnits: units, moveNet: prorateAmount(line.originalNet, line.originalUnits, units) };
    });
  }

  onTotalChange(boundProductId: string, value: string | number): void {
    this.updateLine(boundProductId, (line) => {
      if (line.moveUnits === line.originalUnits && line.moveUnits > 0) {
        // A full-quantity move must take the full net — the backend rejects anything else.
        return { ...line, moveNet: line.originalNet };
      }
      const requested = typeof value === 'number' ? roundMoney(value) : parseMoney(value);
      return { ...line, moveNet: Math.min(Math.max(requested, 0), line.originalNet) };
    });
  }

  // Any change pins the tax type permanently — including clearing the input (parses to 0 and
  // sticks), identical to the product $ override. Clamped so invalid states are unrepresentable;
  // the current side is a derived remainder, so per-type totals are preserved by construction
  // and no nested error modal is needed for taxes.
  onTaxAllocationChange(taxTypeId: string, value: string | number): void {
    // Clamp to the AVAILABLE pool, not the job-level amount: the view already resolves which of
    // the two applies, so this cannot drift from the ceiling the input advertises.
    const tax = this.taxAllocations().find((t) => t.taxTypeId === taxTypeId);
    if (!tax) return;
    const requested = typeof value === 'number' ? roundMoney(value) : parseMoney(value);
    const allocated = Math.min(Math.max(requested, 0), tax.availableAmount);
    this.manualTaxAllocations.update((map) => new Map(map).set(taxTypeId, allocated));
  }

  onSave(): void {
    if (!this.canSave()) {
      this.openResumeOrCancelErrorModal(SPLIT_MINIMUMS_MESSAGE);
      return;
    }
    if (!this.sourceFloorMet()) {
      this.openResumeOrCancelErrorModal(SPLIT_SOURCE_FLOOR_MESSAGE);
      return;
    }
    this.submit();
  }

  cancel(): void {
    this.activeModal.dismiss(false);
  }

  private openResumeOrCancelErrorModal(message: string): void {
    const modalRef = this.modalService.openConfirmModal(
      'Error',
      message,
      'Yes, Resume Split',
      'alert-danger',
      true,
      'btn-primary',
      '',
      'No, Cancel Split',
    );
    modalRef.result.then(
      () => { /* Yes, Resume Split — keep editing */ },
      () => this.activeModal.dismiss(false),
    );
  }

  private isSourceLine(line: SplitLineView): boolean {
    return (line.boundProduct.projectId ?? this.projectId()) === this.projectId();
  }

  private updateLine(boundProductId: string, change: (line: SplitLineView) => SplitLineView): void {
    this.lines.update((lines) =>
      lines.map((line) => (line.boundProduct.id === boundProductId ? change(line) : line)),
    );
  }

  private submit(): void {
    const movedLines = this.lines()
      .filter((line) => line.moveUnits > 0)
      .map((line) => ({
        boundProductId: line.boundProduct.id,
        unitsToMove: line.moveUnits,
        netAmountToMove: line.moveNet,
      }));
    // The full vector, zeros included: what the modal displays is exactly what persists
    // (a zero allocation leaves that tax type untouched server-side).
    const taxAllocations = this.taxAllocations().map((tax) => ({
      taxTypeId: tax.taxTypeId,
      amountToNewProject: tax.allocated,
    }));

    this.loading.set(true);
    this.errorMessage.set(null);
    this.projectService
      .splitProject(this.projectId(), movedLines, taxAllocations)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          // Rare (pre-flight validation runs server-side before the split): the split committed
          // but completing the source failed. Keep the modal open so the message is seen; the
          // user finishes via Complete Project.
          if (result.sourceCompletion?.completed === false) {
            this.loading.set(false);
            this.errorMessage.set(SPLIT_COMPLETION_FAILED_MESSAGE);
            return;
          }
          this.activeModal.close(true);
        },
        error: (error: unknown) => {
          this.loading.set(false);
          const detail = this.extractErrorDetail(error);
          this.errorMessage.set(`Failed to split project. ${detail ?? 'Please try again.'}`);
        },
      });
  }

  private extractErrorDetail(error: unknown): string | undefined {
    if (!error || typeof error !== 'object') return undefined;

    const sdkResponse = (error as { response?: unknown }).response;
    if (typeof sdkResponse === 'string' && sdkResponse.length > 0) {
      try {
        const parsed = JSON.parse(sdkResponse) as { detail?: unknown };
        if (typeof parsed.detail === 'string' && parsed.detail.length > 0) return parsed.detail;
      } catch {
        // not JSON, fall through
      }
    }

    const httpDetail = (error as { error?: { detail?: unknown } }).error?.detail;
    return typeof httpDetail === 'string' && httpDetail.length > 0 ? httpDetail : undefined;
  }
}
